// /checkup/ — checks every published post against the publishing rules
// in Grant's guide and lists the ones that need attention.
//
// Reads posts from Ghost's Content API with the public key Ghost already
// puts on every page (the Portal script's data-key / data-api), 100 at a
// time. Post text only ever goes in through textContent. Portrait feature
// images are found by loading a small copy of each one.

const TOPIC_TAGS = ["mind", "training", "racing-insights", "race-preparation",
    "coaching-services", "testimonial", "book", "podcast", "newsletter"];
const SHORT = 300;

const CHECKS = [
    { key: "image", level: "Fix", label: "No feature image",
      help: "Add a landscape picture, at least 2000 pixels wide." },
    { key: "portrait", level: "Fix", label: "Portrait feature image",
      help: "The post header crops tall pictures to a thin strip. Use a landscape one." },
    { key: "stacked", level: "Fix", label: "Picture at the top of the text",
      help: "It sits straight under the feature image. Move it a paragraph or two down." },
    { key: "description", level: "Fix", label: "No excerpt",
      help: "Write an Excerpt so the post has a summary in listings and Google." },
    { key: "tag", level: "Fix", label: "No topic tag first",
      help: "Put Mind, Training or Racing Insights first in the tags." },
    { key: "links", level: "Improve", label: "No links to other articles",
      help: "Link two or three of your other articles in the text." },
    { key: "short", level: "Some day", label: `Short (under ${SHORT} words)`,
      help: "Short is fine when it's one sharp idea, so don't pad it. Grow it when it's something people look up, like a how-to, a plan or a race: add your sessions, numbers or course notes. If two or three short posts make the same point, fold them into one and ask Shawn to redirect the others." },
];

function apiConfig() {
    const s = document.querySelector("script[data-key][data-api]");
    if (!s) return null;
    return { key: s.dataset.key, api: s.dataset.api.replace(/\/?$/, "/") };
}

async function fetchPosts({ key, api }, onPage) {
    const fields = "id,title,url,feature_image,custom_excerpt,published_at,html";
    const posts = [];
    for (let page = 1; page; ) {
        const url = `${api}posts/?key=${key}&limit=100&page=${page}&include=tags&formats=html&fields=${fields}`;
        const res = await fetch(url, { headers: { "Accept-Version": "v6.0" } });
        if (!res.ok) throw new Error(`Ghost said ${res.status}`);
        const data = await res.json();
        posts.push(...data.posts);
        onPage(posts.length, data.meta.pagination.total);
        page = data.meta.pagination.next;
    }
    return posts;
}

function issuesFor(post) {
    const html = post.html || "";
    const slugs = (post.tags || []).map((t) => t.slug);
    const publicTags = (post.tags || []).filter((t) => t.visibility === "public");
    const isPodcast = slugs.includes("podcast") || slugs.includes("hash-podcast");
    const isLetter = slugs.includes("newsletter");
    const isStory = slugs.includes("testimonial");
    const issues = new Set();

    if (!post.feature_image) issues.add("image");
    if (!post.custom_excerpt) issues.add("description");
    if (!publicTags.length || !TOPIC_TAGS.includes(publicTags[0].slug)) issues.add("tag");
    if (post.feature_image && /^\s*<figure class="kg-card kg-image-card/.test(html)) issues.add("stacked");

    // Episodes, letters and athletes' stories aren't essays: no link or length checks.
    if (!isPodcast && !isLetter && !isStory) {
        const doc = new DOMParser().parseFromString(html, "text/html");
        const site = location.hostname.replace(/^www\./, "");
        const internal = [...doc.querySelectorAll("a[href]")].some((a) => {
            let u;
            try { u = new URL(a.getAttribute("href"), location.origin); } catch { return false; }
            if (u.hostname.replace(/^www\./, "") !== site) return false;
            const path = u.pathname.replace(/\/$/, "");
            return path && !/^\/(tag|author|signup|signin|work|hello|ghost|p)(\/|$)/.test(path);
        });
        if (!internal) issues.add("links");
        const words = (doc.body.textContent || "").trim().split(/\s+/).filter(Boolean).length;
        if (words < SHORT) issues.add("short");
    }
    return issues;
}

// A small copy of a feature image, enough to read its shape.
function thumb(src) {
    if (/\/content\/images\//.test(src) && !/\/size\//.test(src)) {
        return src.replace("/content/images/", "/content/images/size/w100/");
    }
    if (/images\.unsplash\.com/.test(src)) {
        const u = new URL(src);
        u.searchParams.set("w", "100");
        return u.toString();
    }
    return src;
}

function isPortrait(src) {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve(img.naturalHeight > img.naturalWidth * 1.05);
        img.onerror = () => resolve(false);
        img.src = thumb(src);
    });
}

async function checkPortraits(rows, done) {
    const queue = rows.filter((r) => r.post.feature_image);
    const workers = Array.from({ length: 6 }, async () => {
        while (queue.length) {
            const r = queue.shift();
            if (await isPortrait(r.post.feature_image)) r.issues.add("portrait");
        }
    });
    await Promise.all(workers);
    done();
}

function el(tag, cls, text) {
    const n = document.createElement(tag);
    if (cls) n.className = cls;
    if (text != null) n.textContent = text;
    return n;
}

export default function checkup() {
    const root = document.querySelector("[data-checkup]");
    if (!root) return;

    const status = root.querySelector("[data-checkup-status]");
    const filters = root.querySelector("[data-checkup-filters]");
    const list = root.querySelector("[data-checkup-list]");
    const config = apiConfig();
    const editBase = `${location.origin}/ghost/#/editor/post/`;
    let rows = [];
    let active = null;

    function render() {
        const counts = Object.fromEntries(CHECKS.map((c) => [c.key, 0]));
        rows.forEach((r) => r.issues.forEach((k) => counts[k]++));

        filters.replaceChildren();
        const all = el("button", "gg-check__filter", "");
        all.type = "button";
        all.setAttribute("aria-pressed", String(active === null));
        all.append(el("span", "gg-check__filter-label", "Everything"),
            el("span", "gg-check__filter-count", String(rows.filter((r) => r.issues.size).length)));
        all.addEventListener("click", () => { active = null; render(); });
        filters.append(all);
        CHECKS.forEach((c) => {
            const b = el("button", `gg-check__filter gg-check__filter--${c.level.replace(" ", "-").toLowerCase()}`, "");
            b.type = "button";
            b.setAttribute("aria-pressed", String(active === c.key));
            b.append(el("span", "gg-check__filter-level", c.level),
                el("span", "gg-check__filter-label", c.label),
                el("span", "gg-check__filter-count", String(counts[c.key])));
            b.disabled = counts[c.key] === 0;
            b.addEventListener("click", () => { active = active === c.key ? null : c.key; render(); });
            filters.append(b);
        });
        filters.hidden = false;

        const shown = rows
            .filter((r) => (active ? r.issues.has(active) : r.issues.size))
            .sort((a, b) => b.fixCount - a.fixCount || b.issues.size - a.issues.size || b.date - a.date);

        list.replaceChildren();
        if (active) {
            const c = CHECKS.find((x) => x.key === active);
            const tip = el("li", "gg-check__tip");
            tip.append(el("strong", null, `${c.label}. `), document.createTextNode(c.help));
            list.append(tip);
        }
        if (!shown.length) {
            list.append(el("li", "gg-check__empty", active ? "Nothing left for this check." : "Every post passes. Nice work."));
        }
        shown.forEach((r) => {
            const li = el("li", "gg-check__item");
            const title = el("a", "gg-check__post", r.post.title || "(Untitled)");
            title.href = r.post.url;
            const meta = el("p", "gg-check__meta",
                `${new Date(r.post.published_at).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" })}` +
                (r.post.tags && r.post.tags[0] ? ` · ${r.post.tags[0].name}` : ""));
            const chips = el("ul", "gg-check__chips");
            CHECKS.filter((c) => r.issues.has(c.key)).forEach((c) => {
                chips.append(el("li", `gg-check__chip gg-check__chip--${c.level.replace(" ", "-").toLowerCase()}`, c.label));
            });
            const edit = el("a", "gg-check__edit", "Edit in Ghost ");
            edit.href = editBase + r.post.id;
            edit.target = "_blank";
            edit.rel = "noopener";
            edit.append(el("span", "gg-arrow", "→"));
            li.append(title, meta, chips, edit);
            list.append(li);
        });
    }

    if (!config) {
        status.textContent = "Couldn't find the site's reading key, so the checkup can't run. Tell Shawn.";
        return;
    }

    fetchPosts(config, (n, total) => { status.textContent = `Checking posts… ${n} of ${total}`; })
        .then((posts) => {
            rows = posts.map((post) => {
                const issues = issuesFor(post);
                return { post, issues, date: new Date(post.published_at).getTime(),
                    get fixCount() { return CHECKS.filter((c) => c.level === "Fix" && this.issues.has(c.key)).length; } };
            });
            const summary = () => {
                const need = rows.filter((r) => r.issues.size).length;
                status.textContent = `${posts.length} published posts checked. ${need} could use some attention.`;
            };
            summary();
            status.textContent += " Checking picture shapes…";
            render();
            checkPortraits(rows, () => { summary(); render(); });
        })
        .catch((err) => {
            status.textContent = `The checkup couldn't load the posts (${err.message}). Try again in a minute.`;
        });
}
